import React from 'react';
import { Heart, Send, Share2, MoreVertical, PenSquare, CalendarPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import VerifiedAvatar from '../../../components/VerifiedAvatar';

const HeaderCard = ({
    profile,
    profileTitle,
    categoryPath,
    hasStandard,
    buildCategoryHref,
    onShare,
    liked,
    likeCount,
    onToggleLike,
    likeDisabled,
    canLike,
    shouldShowMessageButton,
    hasFull,
    canMessage,
    onOpenMessage,
    user,
    canCreatePost,
    onCreatePost,
    canCreateEvent
}) => {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const menuRef = React.useRef(null);

    React.useEffect(() => {
        if (!menuOpen) return;
        function onDocClick(e) {
            const el = menuRef.current;
            if (!el) return;
            if (el.contains(e.target)) return;
            setMenuOpen(false);
        }
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [menuOpen]);

    return (
        <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-4 min-w-0">
                <div className="flex-shrink-0">
                    <VerifiedAvatar
                        user={profile}
                        userId={profile?.id}
                        src={profile.avatar_url}
                        alt={profile.username}
                        size={80}
                        className="shadow-soft"
                        fallbackText={(profile?.username || '?').charAt(0)}
                        badgePositionClass="bottom-0 right-0"
                    />
                </div>

                <div className="min-w-0">
                    {profileTitle ? (
                        <h1 className="text-2xl font-bold text-primary-500 truncate">{profileTitle}</h1>
                    ) : null}

                    <div className={`${profileTitle ? 'text-sm' : 'text-2xl font-bold'} text-secondary-900 truncate`}>
                        @{profile.username}
                    </div>

                    {profile.slogan ? (
                        <h2 className="text-sm font-bold text-secondary-700 truncate">{profile.slogan}</h2>
                    ) : null}
                </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 w-full justify-end sm:w-auto">
                <button
                    type="button"
                    onClick={onShare}
                    className="p-2 rounded-lg border border-secondary-200 bg-white hover:bg-secondary-50 transition"
                    title="Поделиться"
                >
                    <Share2 className="w-4 h-4 text-secondary-700" />
                </button>

                <button
                    type="button"
                    disabled={likeDisabled}
                    onClick={onToggleLike}
                    className={`inline-flex items-center gap-1 px-3 py-2 rounded-full border text-sm transition ${liked ? 'bg-red-50 border-red-200 text-red-700' : 'bg-white border-secondary-200 text-secondary-700'} ${!user ? 'opacity-60' : 'hover:bg-secondary-50'}`}
                    title={!user ? 'Login erforderlich' : liked ? 'Unlike' : 'Like'}
                >
                    <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
                    <span>{likeCount}</span>
                </button>

                {user?.id && String(user.id) === String(profile?.id) ? (
                    <div className="relative" ref={menuRef}>
                        <button
                            type="button"
                            className="p-2 rounded-lg border border-secondary-200 bg-white hover:bg-secondary-50 transition"
                            title="Меню"
                            aria-label="Меню"
                            onClick={() => setMenuOpen((v) => !v)}
                        >
                            <MoreVertical className="w-4 h-4 text-secondary-700" />
                        </button>

                        {menuOpen ? (
                            <div className="absolute right-0 mt-2 w-56 rounded-xl border border-secondary-200 bg-white shadow-soft overflow-hidden z-50">
                                <Link
                                    to="/settings"
                                    className="w-full px-4 py-3 text-sm text-secondary-800 hover:bg-secondary-50 flex items-center gap-2"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    <span className="w-5 inline-flex items-center justify-center">
                                        <span className="text-secondary-500">⚙</span>
                                    </span>
                                    Настройки
                                </Link>

                                {canCreatePost ? (
                                    <button
                                        type="button"
                                        className="w-full px-4 py-3 text-sm text-secondary-800 hover:bg-secondary-50 flex items-center gap-2"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onCreatePost?.();
                                        }}
                                    >
                                        <PenSquare className="w-4 h-4 text-secondary-600" />
                                        Создать пост
                                    </button>
                                ) : null}

                                {canCreateEvent ? (
                                    <Link
                                        to="/my-events"
                                        className="w-full px-4 py-3 text-sm text-secondary-800 hover:bg-secondary-50 flex items-center gap-2"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <CalendarPlus className="w-4 h-4 text-secondary-600" />
                                        Создать событие
                                    </Link>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {shouldShowMessageButton && hasFull && (
                    <button
                        type="button"
                        onClick={onOpenMessage}
                        className={`btn btn-primary ${!canMessage ? 'opacity-60' : ''}`}
                        title="Написать сообщение"
                    >
                        <Send className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default HeaderCard;
